import { Link } from "react-router-dom";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { formatDateOnly } from "../../services/hiveActions";
import { groupTasks, reasonFor, tasksBySite } from "./taskUrgency";

/**
 * Tasks arranged so that thirty of them are still readable (#283).
 *
 * Two arrangements of the same data, because they answer different
 * questions and a single compromise view answers neither well:
 *
 *   "when"  Today / This week / Later, ranked by consequence inside each.
 *           For deciding what to do next.
 *   "site"  gathered by yard, yards ordered by their most urgent task.
 *           For a trip -- at this scale somebody drives somewhere and
 *           does everything there.
 *
 * Neither shows the urgency score. A beekeeper needs to know WHY a task
 * is at the top, and a number does not say.
 */

const GROUP_TITLES = {
  today: "Today",
  week: "This week",
  later: "Later",
};

const GROUP_NOTES = {
  // Said once, so nobody wonders where the overdue pile went.
  today: "Including anything overdue",
  later: "Undated work lives here too",
};

/*
 * A row used to end with a status Badge AND a status <select> holding the
 * same word, side by side. Read left to right that was
 *
 *     [No date] Inspection [Pending]  [Pending v]
 *
 * -- three pill-shaped things and a dropdown, which is what a filter bar
 * looks like. It was reported as a stray row of filters at the bottom of
 * the list, and that is a fair reading of it.
 *
 * The select is both the display and the control now, so the state is
 * stated once. The reason badge stays because it says something the
 * status does not: WHY this task is where it is in the order.
 */
function TaskRow({ task, issuesByHive, statuses, statusTone, onStatus, onDelete, canManage, siteInLabel = true }) {
  const reason = reasonFor(task, { issuesByHive });
  // A status the account is not offered -- Overdue, or anything a future
  // migration adds -- must still be selectable-as-current, or the select
  // silently renders the first option instead and the row claims a state
  // the task is not in.
  const options = statuses.includes(task.status) ? statuses : [task.status, ...statuses];
  return (
    <li
      style={{
        display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap",
        padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)",
      }}
    >
      {reason && <Badge tone={reason.tone}>{reason.label}</Badge>}
      <strong>{task.task_type}</strong>
      {/*
          WHICH HIVE. Reported from dev: two overdue tasks that said only
          "Inspection", and a "DoubleDoseReview" with no way to tell what
          it was about. A task type on its own is a category, not a job --
          nobody can act on "Inspection" without knowing where.

          GET /team/tasks has returned hive_number and dock_name the whole
          time; the row simply never rendered them.

          The yard is dropped in the BY SITE view because it is the
          heading directly above -- the same reasoning as the harvest
          form's hive picker, where repeating the yard on every row buries
          the number that actually differs.
       */}
      {task.hive_number ? (
        /*
         * A LINK, not a label. Reported from dev: "if we're going to draw
         * attention to it here they should be able to work it from that
         * screen or at least navigate to it." A row that tells you a hive
         * is seven days overdue and then makes you go and find it is
         * doing half a job.
         *
         * Link rather than an onClick on the row: the row already holds a
         * status select and a Delete, so making the whole thing clickable
         * would put a navigation target under two controls that are not
         * one -- and a real anchor can be opened in a new tab, which is
         * what somebody working a list of five overdue hives will want.
         */
        <Link
          to={`/hives/${task.hive_id}`}
          style={{ color: "var(--color-primary)", fontSize: "var(--font-size-sm)" }}
        >
          {siteInLabel && task.site_name ? `${task.site_name} — ` : ""}Hive {task.hive_number}
        </Link>
      ) : task.site_id != null && task.site_name ? (
        /*
         * A YARD, WHICH IS A REAL ANSWER (#393). "Inspect the hives at
         * Site 7" is a normal instruction, and since V101 the form can
         * say it -- so this used to read "No hive" about a task that
         * knows exactly where it is.
         *
         * Not a link: there is no single hive to open, and guessing one
         * would send somebody to the wrong colony.
         */
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          {siteInLabel ? "The whole site" : `${task.site_name} — the whole site`}
        </span>
      ) : (
        /*
         * Said out loud rather than left blank. The task genuinely has no
         * location -- both columns are optional and the assign form does
         * not insist -- so an empty space here reads as the screen hiding
         * something, when the truth is the task never recorded it.
         * "Inspection" with no hive is a task nobody can act on, and
         * seeing that is the first step to it not happening again.
         */
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)", fontStyle: "italic" }}>
          No hive
        </span>
      )}
      {task.due_date && (
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          {formatDateOnly(task.due_date)}
        </span>
      )}
      {task.description && (
        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          {task.description}
        </span>
      )}
      <div style={{ display: "flex", gap: "var(--space-2)", marginLeft: "auto", alignItems: "center" }}>
        <select id="ranked-task-list-status" name="status"
          value={task.status}
          onChange={(e) => onStatus(task.task_id, e.target.value)}
          className="touch-target"
          aria-label={`Status for ${task.task_type}`}
          // Tinted to the status it holds, so the colour the Badge used
          // to carry is not lost along with the duplication.
          data-tone={statusTone[task.status]}
        >
          {options.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {canManage && (
          <Button size="sm" variant="danger" onClick={() => onDelete(task)}>Delete</Button>
        )}
      </div>
    </li>
  );
}

export function RankedTaskList({
  tasks, hives = [], issuesByHive = {}, view = "when",
  statuses = [], statusTone = {}, onStatus, onDelete, canManage = false,
  emptyTitle = "No tasks yet", emptyDescription = "Assign one to get started.",
}) {
  if (!tasks || tasks.length === 0) {
    // The caller supplies the wording because the list is filtered above
    // it: "No tasks yet -- assign one to get started" is wrong and mildly
    // alarming when the account has forty tasks and the filter is set to
    // Completed.
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const rowProps = { issuesByHive, statuses, statusTone, onStatus, onDelete, canManage };

  if (view === "site") {
    const sites = tasksBySite(tasks, hives, { issuesByHive });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        {sites.map((site) => (
          <section key={site.site_id ?? site.site_name}>
            <h3 style={{ margin: "0 0 var(--space-1)", fontSize: "var(--font-size-md)" }}>
              {site.site_name}
              <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>
                {" "}— {site.tasks.length} {site.tasks.length === 1 ? "task" : "tasks"}
              </span>
            </h3>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {site.tasks.map((task) => (
                <TaskRow key={task.task_id} task={task} {...rowProps} siteInLabel={false} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    );
  }

  const groups = groupTasks(tasks, { issuesByHive });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {["today", "week", "later"].map((key) => {
        // An empty bucket is left out rather than shown empty: three
        // headings with nothing under two of them reads as broken.
        if (groups[key].length === 0) return null;
        return (
          <section key={key}>
            <h3 style={{ margin: "0 0 var(--space-1)", fontSize: "var(--font-size-md)" }}>
              {GROUP_TITLES[key]}
              <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>
                {" "}({groups[key].length})
              </span>
            </h3>
            {GROUP_NOTES[key] && (
              <p style={{ margin: "0 0 var(--space-1)", color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                {GROUP_NOTES[key]}
              </p>
            )}
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {groups[key].map((task) => (
                <TaskRow key={task.task_id} task={task} {...rowProps} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
